/**
 * Utility functions for converting HTML content to plain text
 * Used for migrating existing forum content from rich text to plain text
 */

/**
 * Convert HTML content to plain text while preserving structure
 * @param {string} htmlContent - HTML string to convert
 * @returns {string} - Plain text content
 */
export const convertHtmlToPlainText = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return '';
  }

  // If it's already plain text (no HTML tags), return as is
  if (!htmlContent.includes('<')) {
    return htmlContent.trim();
  }

  try {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Convert lists to plain text with bullet points
    const lists = tempDiv.querySelectorAll('ul, ol');
    lists.forEach(list => {
      const items = list.querySelectorAll('li');
      const isOrdered = list.tagName === 'OL';
      
      const plainList = Array.from(items).map((item, index) => {
        const prefix = isOrdered ? `${index + 1}. ` : '• ';
        return `${prefix}${item.textContent || item.innerText || ''}`;
      }).join('\n');
      
      // Replace the list with plain text
      const textNode = document.createTextNode('\n' + plainList + '\n');
      list.parentNode.replaceChild(textNode, list);
    });

    // Extract and format links
    const links = tempDiv.querySelectorAll('a[href]');
    links.forEach(link => {
      const url = link.getAttribute('href');
      const text = link.textContent || link.innerText || '';
      
      // If link text is different from URL, show both
      if (text && text !== url && !text.includes(url)) {
        const linkText = document.createTextNode(`${text} (${url})`);
        link.parentNode.replaceChild(linkText, link);
      } else {
        // Just show the URL
        const linkText = document.createTextNode(url);
        link.parentNode.replaceChild(linkText, link);
      }
    });

    // Convert line breaks and paragraphs
    const brs = tempDiv.querySelectorAll('br');
    brs.forEach(br => {
      const newline = document.createTextNode('\n');
      br.parentNode.replaceChild(newline, br);
    });

    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      const content = p.textContent || p.innerText || '';
      if (content.trim()) {
        const textWithNewlines = document.createTextNode('\n' + content + '\n');
        p.parentNode.replaceChild(textWithNewlines, p);
      } else {
        p.remove();
      }
    });

    // Handle code blocks
    const codeBlocks = tempDiv.querySelectorAll('pre, code');
    codeBlocks.forEach(code => {
      const content = code.textContent || code.innerText || '';
      const codeText = document.createTextNode(`\n${content}\n`);
      code.parentNode.replaceChild(codeText, code);
    });

    // Get the final plain text
    let plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up extra whitespace and newlines
    plainText = plainText
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple newlines with double newlines
      .replace(/^\s+|\s+$/g, '') // Trim whitespace from start and end
      .replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space

    return plainText;
  } catch (error) {
    console.error('Error converting HTML to plain text:', error);
    
    // Fallback: strip HTML tags using regex
    return htmlContent
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
      .replace(/&amp;/g, '&') // Replace HTML entities
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .trim();
  }
};

/**
 * Detect URLs in plain text and return them as an array
 * @param {string} text - Plain text to search for URLs
 * @returns {Array} - Array of detected URLs
 */
export const detectUrls = (text) => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;
  const matches = text.match(urlRegex);
  
  return matches || [];
};

/**
 * Convert plain text with URLs to HTML with clickable links
 * Used for displaying content with auto-detected links
 * @param {string} plainText - Plain text content
 * @returns {string} - HTML with clickable links
 */
export const renderTextWithLinks = (plainText) => {
  if (!plainText || typeof plainText !== 'string') {
    return '';
  }

  const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;
  
  // Escape HTML characters first
  let escapedText = plainText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // Convert URLs to clickable links
  const htmlWithLinks = escapedText.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline break-words" data-external-link="true">${url}</a>`;
  });

  // Convert line breaks to <br> tags
  return htmlWithLinks.replace(/\n/g, '<br>');
};

/**
 * Validate that content is plain text (no HTML)
 * @param {string} content - Content to validate
 * @returns {boolean} - True if content is plain text
 */
export const isPlainText = (content) => {
  if (!content || typeof content !== 'string') {
    return true;
  }

  // Check if content contains HTML tags
  const htmlTagRegex = /<[^>]*>/;
  return !htmlTagRegex.test(content);
};

/**
 * Clean and normalize plain text content
 * @param {string} content - Content to clean
 * @returns {string} - Cleaned content
 */
export const cleanPlainText = (content) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  return content
    .trim()
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines to 2
    .replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space
};

/**
 * Get plain text length for validation
 * @param {string} content - Content to measure
 * @returns {number} - Character count
 */
export const getPlainTextLength = (content) => {
  if (!content || typeof content !== 'string') {
    return 0;
  }

  return content.length;
};

/**
 * Truncate plain text to specified length
 * @param {string} content - Content to truncate
 * @param {number} maxLength - Maximum length
 * @param {string} suffix - Suffix to add when truncated (default: '...')
 * @returns {string} - Truncated content
 */
export const truncatePlainText = (content, maxLength, suffix = '...') => {
  if (!content || typeof content !== 'string' || content.length <= maxLength) {
    return content || '';
  }

  const truncated = content.substring(0, maxLength - suffix.length);
  
  // Try to break at word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace) + suffix;
  }
  
  return truncated + suffix;
};